/**
 * Inline editor for a provider's per-scope region allow-list.
 *
 * Renders as a tag-style list of region chips with an inline "+ add"
 * input. Region values are free-form (so an operator can paste a
 * brand-new region the day SkyPilot ships it). Type-ahead suggestions
 * are passed in via `suggestions`; callers should source those from
 * the live `useCloudRegions(kind)` hook in `api/local/providers.ts`,
 * which proxies to SkyPilot's own catalog through the controller —
 * the WebUI deliberately does not maintain a parallel hand-edited
 * region list.
 *
 * Entries support two shapes, both stored verbatim in the underlying
 * `enabled_regions` CSV:
 *
 *   - `REGION`         — the common case. SkyPilot picks any zone
 *                        inside the region.
 *   - `REGION:ZONE`    — pin to a specific availability zone. Used
 *                        as a workaround for SkyPilot's RunPod
 *                        adapter, which joins every catalog zone for
 *                        a region into one comma-separated string and
 *                        then re-validates it as a single zone (fails
 *                        with "Invalid zone 'A,B,C' for region X" on
 *                        any multi-DC RunPod region — US, CA, IS in
 *                        the current catalog). See the controller's
 *                        `controller/skypilot/placements.py` module
 *                        docstring for the full rationale.
 *
 * Zone-pinned chips render with a slightly different shape (the zone
 * suffix is split out as a smaller monospace tail) so operators can
 * tell at a glance which entries are bypassing the zone-join bug.
 *
 * The component is dumb about persistence: it owns no state beyond
 * the in-progress draft text. The parent passes `regions` + `onChange`
 * and decides whether the regions live in the global / service / pool
 * scope and how they get persisted (see `regionsToValuePatch` in
 * `api/local/providers.ts`).
 *
 * When `readOnly` is true the editor renders the chips but disables
 * the add input and × buttons — used to show an inherited list at a
 * lower scope where the operator hasn't overridden it yet.
 */
import { useId, useState, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";

import { parseRegionZone } from "@/api/local/providers";
import { Pill } from "@/components/Pill";

interface RegionsCellProps {
  regions: string[];
  onChange: (next: string[]) => void;
  /** Known region values for this provider (purely a UX hint). */
  suggestions?: ReadonlyArray<{ value: string; label?: string }>;
  /** Disable mutation — used for inherited rows. */
  readOnly?: boolean;
  /** Shown when the list is empty (e.g. "all regions"). */
  emptyLabel?: string;
  /** Optional placeholder text for the add-region input. */
  placeholder?: string;
  /** Optional className override on the wrapping div. */
  className?: string;
}

export function RegionsCell({
  regions,
  onChange,
  suggestions,
  readOnly = false,
  emptyLabel = "all regions",
  placeholder = "add region or REGION:ZONE…",
  className,
}: RegionsCellProps) {
  const [draft, setDraft] = useState("");
  const listId = useId();

  const commit = (raw: string) => {
    const cleaned = raw.trim();
    if (!cleaned) return;
    // Split on whitespace and commas so operators can paste
    // "us-east-1, us-west-2" in one go. We deliberately do NOT split
    // on `:` — that's the REGION:ZONE separator and must survive.
    const parts = cleaned
      .split(/[\s,]+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length === 0) return;
    const merged = Array.from(new Set([...regions, ...parts]));
    onChange(merged);
    setDraft("");
  };

  const remove = (value: string) => {
    onChange(regions.filter((r) => r !== value));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Comma triggers commit (paste-friendly), but `:` must NOT —
    // operators are mid-typing a `REGION:ZONE` entry when they hit it.
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && regions.length > 0) {
      // Backspace-on-empty drops the last chip — matches the standard
      // tag-input idiom (GitHub topic input, Linear labels, etc.).
      e.preventDefault();
      remove(regions[regions.length - 1]);
    }
  };

  return (
    <div
      className={`regions-cell${readOnly ? " regions-cell--readonly" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      {regions.length === 0 && (
        <span className="regions-cell__empty muted">{emptyLabel}</span>
      )}
      {regions.map((r) => {
        const { region, zone } = parseRegionZone(r);
        const tone = zone ? "warn" : "info";
        // Pinned chips get a "warn" tone so they visually pop next to
        // bare-region chips — they're not failures, but they ARE the
        // operator opting out of the default "any zone in this region"
        // behaviour, and we want that to be obvious in glance reviews.
        const title = zone
          ? `pinned to availability zone ${zone} in ${region}`
          : `region ${region}`;
        return (
          <span key={r} className="regions-cell__chip" title={title}>
            <Pill tone={tone}>
              {zone ? (
                <>
                  <span className="regions-cell__chip-region">{region}</span>
                  <span className="regions-cell__chip-sep">:</span>
                  <span className="regions-cell__chip-zone">{zone}</span>
                </>
              ) : (
                region
              )}
            </Pill>
            {!readOnly && (
              <button
                type="button"
                className="regions-cell__remove"
                onClick={() => remove(r)}
                aria-label={`remove ${r}`}
                title={`remove ${r}`}
              >
                <X />
              </button>
            )}
          </span>
        );
      })}
      {!readOnly && (
        <span className="regions-cell__input-wrap">
          <input
            className="regions-cell__input mono"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => commit(draft)}
            placeholder={placeholder}
            list={suggestions && suggestions.length > 0 ? listId : undefined}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          {draft.length > 0 && (
            <button
              type="button"
              className="regions-cell__add"
              onClick={() => commit(draft)}
              aria-label="add region"
              title="add region"
            >
              <Plus />
            </button>
          )}
          {suggestions && suggestions.length > 0 && (
            <datalist id={listId}>
              {suggestions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label && s.label !== s.value ? s.label : undefined}
                </option>
              ))}
            </datalist>
          )}
        </span>
      )}
    </div>
  );
}
